import { supabase } from '../lib/supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface UnreadMessage {
  id: string;
  message: string;
  senderName: string;
  senderEmail: string;
  createdAt: string;
}

type MessageCallback = (message: UnreadMessage) => void;

class ChatNotificationService {
  private teamUnreadCount: number = 0;
  private privateUnreadCount: number = 0;
  private lastSeenTimestamp: string | null = null;
  private listeners: Set<MessageCallback> = new Set();
  private teamChannel: RealtimeChannel | null = null;
  private privateChannel: RealtimeChannel | null = null;
  private userId: string | null = null;

  constructor() {
    this.loadLastSeenTimestamp();
    this.loadUnreadCount();
  }

  // Initialize the service with current user
  async initialize() {
    console.log('[ChatNotification] Initializing service...');
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      this.userId = user.id;
      console.log('[ChatNotification] User ID:', this.userId);
      await this.subscribeToTeamMessages();
      await this.subscribeToPrivateMessages();
      await this.updateUnreadCount();
      console.log('[ChatNotification] Service initialized. Team unread:', this.teamUnreadCount, 'Private unread:', this.privateUnreadCount);
    } else {
      console.log('[ChatNotification] No authenticated user found');
    }
  }

  // Load last seen timestamp from localStorage
  private loadLastSeenTimestamp() {
    const stored = localStorage.getItem('teamChatLastSeen');
    if (stored) {
      this.lastSeenTimestamp = stored;
    }
  }

  // Load unread count from localStorage
  private loadUnreadCount() {
    const stored = localStorage.getItem('teamChatUnreadCount');
    if (stored) {
      this.teamUnreadCount = parseInt(stored, 10) || 0;
    }
    const privateStored = localStorage.getItem('privateMessagesUnreadCount');
    if (privateStored) {
      this.privateUnreadCount = parseInt(privateStored, 10) || 0;
    }
  }

  // Save last seen timestamp to localStorage
  private saveLastSeenTimestamp(timestamp: string) {
    this.lastSeenTimestamp = timestamp;
    localStorage.setItem('teamChatLastSeen', timestamp);
  }

  // Save unread count to localStorage
  private saveUnreadCount(count: number) {
    this.teamUnreadCount = count;
    localStorage.setItem('teamChatUnreadCount', count.toString());
  }

  private savePrivateUnreadCount(count: number) {
    this.privateUnreadCount = count;
    localStorage.setItem('privateMessagesUnreadCount', count.toString());
  }

  // Subscribe to team messages in real-time
  private async subscribeToTeamMessages() {
    if (!this.userId) {
      console.log('[ChatNotification] Cannot subscribe - no user ID');
      return;
    }

    console.log('[ChatNotification] Setting up real-time subscription for user:', this.userId);

    // Get user's organization first (same pattern as TeamChatService)
    const { data: membership, error: membershipError } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', this.userId)
      .eq('is_active', true)
      .maybeSingle();

    if (membershipError || !membership) {
      console.log('[ChatNotification] No organization membership found:', membershipError);
      return;
    }

    console.log('[ChatNotification] Subscribing to organization:', membership.organization_id);

    // Create subscription with organization filter (same as TeamChatService)
    this.teamChannel = supabase
      .channel(`team_chat_notifications:${membership.organization_id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_messages',
          filter: `organization_id=eq.${membership.organization_id}` // Filter by org
        },
        async (payload) => {
          console.log('[ChatNotification] New message received:', payload);
          const newMessage = payload.new as any;
          
          // Don't notify for own messages
          if (newMessage.user_id === this.userId) {
            console.log('[ChatNotification] Ignoring own message');
            return;
          }

          // Increment unread count
          const newCount = this.teamUnreadCount + 1;
          this.saveUnreadCount(newCount);
          console.log('[ChatNotification] Unread count updated to:', newCount);

          // Get sender details from message
          const senderEmail = newMessage.sender_email || 'unknown@email.com';
          const senderName = newMessage.sender_name || senderEmail.split('@')[0] || 'Team Member';

          const unreadMessage: UnreadMessage = {
            id: newMessage.id,
            message: newMessage.content, // team_messages uses 'content' not 'message'
            senderName,
            senderEmail,
            createdAt: newMessage.created_at
          };

          console.log('[ChatNotification] Notifying listeners:', unreadMessage);
          // Notify all listeners
          this.listeners.forEach(callback => callback(unreadMessage));
        }
      )
      .subscribe((status) => {
        console.log('[ChatNotification] Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('[ChatNotification] ✅ Real-time subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.log('[ChatNotification] ❌ Team subscription error');
        }
      });
  }

  // Subscribe to private messages in real-time
  private async subscribeToPrivateMessages() {
    if (!this.userId) {
      console.log('[ChatNotification] Cannot subscribe to private messages - no user ID');
      return;
    }

    console.log('[ChatNotification] Setting up private message subscription for user:', this.userId);

    // Subscribe to all private messages where user is recipient
    this.privateChannel = supabase
      .channel(`private_message_notifications:${this.userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_messages',
          filter: `recipient_id=eq.${this.userId}` // Only messages sent TO this user
        },
        async (payload) => {
          console.log('[ChatNotification] New private message received:', payload);
          const newMessage = payload.new as any;

          // Increment private unread count
          const newCount = this.privateUnreadCount + 1;
          this.savePrivateUnreadCount(newCount);
          console.log('[ChatNotification] Private unread count updated to:', newCount);

          // Get sender details from message
          const senderName = newMessage.sender_name || 'Team Member';

          const unreadMessage: UnreadMessage = {
            id: newMessage.id,
            message: newMessage.content,
            senderName,
            senderEmail: '',
            createdAt: newMessage.created_at
          };

          console.log('[ChatNotification] Notifying listeners (private):', unreadMessage);
          // Notify all listeners
          this.listeners.forEach(callback => callback(unreadMessage));
        }
      )
      .subscribe((status) => {
        console.log('[ChatNotification] Private subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('[ChatNotification] ✅ Private message subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[ChatNotification] ❌ Private subscription error');
        }
      });
  }

  // Mark messages as read
  markAsRead() {
    const now = new Date().toISOString();
    this.saveLastSeenTimestamp(now);
    this.saveUnreadCount(0);
    this.savePrivateUnreadCount(0);
  }

  // Get current unread count (team messages)
  getUnreadCount(): number {
    return this.teamUnreadCount;
  }

  // Get private messages unread count
  getPrivateUnreadCount(): number {
    return this.privateUnreadCount;
  }

  // Get total unread count (team + private)
  getTotalUnreadCount(): number {
    return this.teamUnreadCount + this.privateUnreadCount;
  }

  // Update unread count by querying database
  async updateUnreadCount() {
    if (!this.userId || !this.lastSeenTimestamp) {
      this.saveUnreadCount(0);
      this.savePrivateUnreadCount(0);
      return;
    }

    try {
      const { data: teamMessages, error: teamError } = await supabase
        .from('team_messages')
        .select('id')
        .neq('user_id', this.userId) // Exclude own messages
        .gt('created_at', this.lastSeenTimestamp);

      if (teamError) throw teamError;

      this.saveUnreadCount(teamMessages?.length || 0);

      const { data: privateMessages, error: privateError } = await supabase
        .from('private_messages')
        .select('id')
        .eq('recipient_id', this.userId)
        .gt('created_at', this.lastSeenTimestamp);

      if (privateError) throw privateError;

      this.savePrivateUnreadCount(privateMessages?.length || 0);
    } catch (error) {
      console.error('[ChatNotification] Failed to update unread count:', error);
    }
  }

  // Add listener for new messages
  addListener(callback: MessageCallback) {
    this.listeners.add(callback);
  }

  // Remove listener
  removeListener(callback: MessageCallback) {
    this.listeners.delete(callback);
  }

  // Cleanup
  cleanup() {
    if (this.teamChannel) {
      supabase.removeChannel(this.teamChannel);
    }
    if (this.privateChannel) {
      supabase.removeChannel(this.privateChannel);
    }
    this.listeners.clear();
  }
}

// Export singleton instance
export const chatNotificationService = new ChatNotificationService();
export type { UnreadMessage };
